import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Button, Card, Dropdown, Empty, Menu, Modal } from 'antd';
import {
	CheckOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	FileTextOutlined,
	MoreOutlined,
} from '@ant-design/icons';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';
import { complianceDocumentsRestCrudThunks } from '../../thunks/compliance_documents_thunks';
import { complianceTypesRestCrudThunks } from '../../thunks/compliance_types_thunks';
import { fetchComplianceSummary } from '../../thunks/compliance_request_instances_thunks';
import { fetchComplianceClientStatuses } from '../../thunks/compliance_statuses_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	ComplianceDocument,
	ComplianceType,
	COMPLIANCE_EXPIRY_STATUSES,
} from '../../types/ComplianceTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { deriveComplianceExpiryStatus } from '../../utils/ComplianceUtils';
import {
	COMPLIANCE_EXPIRY_STATUS_COLORS,
	COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES,
} from '../compliance_common/ComplianceConstants';
import AddComplianceDocumentModal from './AddComplianceDocumentModal';
import ManageDocumentClientsModal from './ManageDocumentClientsModal';
import DocumentClientApprovalStatusCounts from './DocumentClientApprovalStatusCounts';
import useMountEffect from '../../hooks/useMountEffect';

const DOCUMENTS_INDEX_TARGET_COLLECTION = 'supplierComplianceDocumentsIndex';
const DOCUMENTS_COLLECTION = 'compliance_documents';
const TYPES_TARGET_COLLECTION = 'supplierComplianceDocumentsTypes';
const INITIAL_PAGINATION = { current: 1, pageSize: 10 };

// check / clock / x icons for the derived expiry status.
const EXPIRY_ICONS = {
	[COMPLIANCE_EXPIRY_STATUSES.UP_TO_DATE]: CheckOutlined,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRING_SOON]: ClockCircleOutlined,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRED]: CloseCircleOutlined,
};

const formatDate = (isoDate) => moment(isoDate).format('D MMM, YYYY');

// PaginatedReduxTable dispatches its fetchData prop itself, so it must be the
// raw thunk-returning read method (not a pre-dispatched wrapper).
const fetchDocuments = complianceDocumentsRestCrudThunks(ROLE_TYPES.SUPPLIER).read;

interface SupplierComplianceDocumentsPageProps {
	complianceTypes: ComplianceType[];
	fetchComplianceTypes: () => void;
	refetchDocuments: () => void;
	// Refetch the derived client compliance (shell banner + Overview client badges),
	// which the backend re-derives when a document's client sharing changes.
	refreshComplianceState: () => void;
	deleteComplianceDocument: (doc: ComplianceDocument) => Promise<any>;
}

const SupplierComplianceDocumentsPage: FC<SupplierComplianceDocumentsPageProps> = ({
	complianceTypes,
	fetchComplianceTypes,
	refetchDocuments,
	refreshComplianceState,
	deleteComplianceDocument,
}): React.ReactElement => {
	const [editingDocument, setEditingDocument] = useState(null);
	const [replaceMode, setReplaceMode] = useState(false);
	const [managingClientsDocument, setManagingClientsDocument] = useState(null);

	useMountEffect(() => {
		fetchComplianceTypes();
	});

	const closeEditModal = () => {
		setEditingDocument(null);
		setReplaceMode(false);
	};
	const closeClientsModal = () => setManagingClientsDocument(null);

	const handleEditSuccess = () => {
		setEditingDocument(null);
		setReplaceMode(false);
		refetchDocuments();
		// Editing/replacing a linked document (esp. its expiry) can flip client
		// compliance server-side, so refresh the shell banner + Overview client badges.
		refreshComplianceState();
	};
	const handleClientsSuccess = () => {
		setManagingClientsDocument(null);
		refetchDocuments();
		// Changing which clients a document is shared with can flip client compliance
		// server-side, so refresh the shell banner + Overview client badges too.
		refreshComplianceState();
	};

	const confirmDelete = (doc) => {
		Modal.confirm({
			title: `Delete document "${doc.name}"?`,
			content: 'Clients will lose access to this document.',
			okText: 'Delete',
			okType: 'danger',
			onOk: () =>
				deleteComplianceDocument(doc).then(() => {
					refetchDocuments();
					// Deleting a linked document can flip client compliance server-side.
					refreshComplianceState();
				}),
		});
	};

	// Documents reference GLOBAL compliance types by id only. Resolve the display
	// name from a hydrated `complianceType` if the backend denormalized one,
	// otherwise from the loaded types list. Show `--` when the document is untyped.
	const resolveTypeName = (doc: ComplianceDocument): string => {
		const hydratedName = nullSafeGet('complianceType.name', doc);
		if (hydratedName) {
			return hydratedName;
		}
		if (doc.complianceTypeId) {
			const match = complianceTypes.find((type) => type.id === doc.complianceTypeId);
			if (match) {
				return match.name;
			}
		}
		return '--';
	};

	const renderDocumentDetails = (doc: ComplianceDocument) => (
		<div className="flex flex-col gap-1">
			<div className="bodymedium">{doc.name}</div>
			<div className="footnoteregular text-character-secondary">{resolveTypeName(doc)}</div>
		</div>
	);

	// Derived expiry status + date(s). Documents carry effectiveDate/expiryDate but
	// no server expiryStatus, so the status is derived client-side.
	const renderExpiration = (doc: ComplianceDocument) => {
		const effectiveDate = nullSafeGet('effectiveDate', doc);
		const expiryDate = nullSafeGet('expiryDate', doc);
		if (!effectiveDate && !expiryDate) {
			return <span className="text-character-secondary">--</span>;
		}
		const status = deriveComplianceExpiryStatus(expiryDate);
		const color = status ? COMPLIANCE_EXPIRY_STATUS_COLORS[status] : undefined;
		const IconComponent = status ? EXPIRY_ICONS[status] : null;
		return (
			<div className="flex flex-col gap-1">
				{status ? (
					<span className="bodymedium inline-flex flex-row items-center gap-1" style={{ color }}>
						{IconComponent ? <IconComponent /> : null}
						{COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES[status]}
					</span>
				) : null}
				{effectiveDate && expiryDate ? (
					<>
						<span className="footnoteregular text-character-secondary">
							Effective {formatDate(effectiveDate)}
						</span>
						<span className="footnoteregular text-character-secondary">
							Expiry: {formatDate(expiryDate)}
						</span>
					</>
				) : expiryDate ? (
					<span className="footnoteregular text-character-secondary">{formatDate(expiryDate)}</span>
				) : (
					<span className="footnoteregular text-character-secondary">
						Effective {formatDate(effectiveDate)}
					</span>
				)}
			</div>
		);
	};

	const renderDocLink = (doc: ComplianceDocument) => {
		if (!doc.fileUrl) {
			return <span className="text-character-secondary">--</span>;
		}
		return (
			<a
				className="flex min-w-0 flex-row items-center gap-1"
				href={doc.fileUrl}
				target="_blank"
				rel="noopener noreferrer"
				title={doc.fileName || 'document'}
			>
				<FileTextOutlined className="shrink-0" />
				<span className="truncate">{doc.fileName || 'document'}</span>
			</a>
		);
	};

	const renderRowMenu = (doc: ComplianceDocument) => {
		const handleEditClick = () => {
			setReplaceMode(false);
			setEditingDocument(doc);
		};
		const handleReplaceClick = () => {
			setReplaceMode(true);
			setEditingDocument(doc);
		};
		const handleClientsClick = () => setManagingClientsDocument(doc);
		const handleDeleteClick = () => confirmDelete(doc);
		return (
			<Menu>
				<Menu.Item key="edit" onClick={handleEditClick}>
					Edit Document
				</Menu.Item>
				<Menu.Item key="clients" onClick={handleClientsClick}>
					Add / remove client
				</Menu.Item>
				<Menu.Item key="replace" onClick={handleReplaceClick}>
					Replace Document
				</Menu.Item>
				<Menu.Item key="delete" danger={true} onClick={handleDeleteClick}>
					Delete Document
				</Menu.Item>
			</Menu>
		);
	};

	const tableColumns = [
		{
			title: 'Document details',
			key: 'details',
			width: '28%',
			render: (text, doc) => renderDocumentDetails(doc),
		},
		{
			title: 'Client Approval Status',
			key: 'clientApproval',
			width: '26%',
			render: (text, doc) => <DocumentClientApprovalStatusCounts complianceDocument={doc} />,
		},
		{
			title: 'Expiration Date',
			key: 'expiration',
			width: '20%',
			render: (text, doc) => renderExpiration(doc),
		},
		{
			title: 'Compliance Doc',
			key: 'doc',
			width: '22%',
			render: (text, doc) => renderDocLink(doc),
		},
		{
			title: '',
			key: 'actions',
			width: 48,
			align: 'right' as const,
			render: (text, doc) => (
				<Dropdown overlay={renderRowMenu(doc)} trigger={['click']}>
					<Button icon={<MoreOutlined />} />
				</Dropdown>
			),
		},
	];

	const emptyState = (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description="Your compliance documents will appear here. Add one to share it with clients."
		/>
	);

	return (
		<div className=" pb-6">
			<Card bordered={false} bodyStyle={{ padding: 8 }}>
				<PaginatedReduxTable
					mode="list"
					// Sync filters (incl. search) to the URL so the shell's AdvancedFilters
					// search box clears correctly (it derives initialSearch from the URL).
					updateQueryParams={true}
					showHeader={true}
					collectionName={DOCUMENTS_COLLECTION}
					targetCollectionName={DOCUMENTS_INDEX_TARGET_COLLECTION}
					columns={tableColumns}
					keyAccessor={(doc) => doc.id}
					initialPagination={INITIAL_PAGINATION}
					fetchData={fetchDocuments}
					emptyState={emptyState}
				/>
			</Card>
			{editingDocument && (
				<AddComplianceDocumentModal
					visible={!!editingDocument}
					editingDocument={editingDocument}
					replaceMode={replaceMode}
					onCancel={closeEditModal}
					onSuccess={handleEditSuccess}
				/>
			)}
			{managingClientsDocument && (
				<ManageDocumentClientsModal
					visible={!!managingClientsDocument}
					complianceDocument={managingClientsDocument}
					onCancel={closeClientsModal}
					onSuccess={handleClientsSuccess}
				/>
			)}
		</div>
	);
};

const mapStateToProps = (state) => ({
	complianceTypes: getRecordsForTargetCollection(state.compliance_types, TYPES_TARGET_COLLECTION),
});

const mapDispatchToProps = (dispatch) => ({
	fetchComplianceTypes: () =>
		dispatch(complianceTypesRestCrudThunks(ROLE_TYPES.SUPPLIER).read({}, TYPES_TARGET_COLLECTION)),
	// Replay the collection's current filters + pagination (read at call time via
	// getState) so an edit/replace/delete keeps the user on the same filtered page
	// instead of snapping back to unfiltered page 1 under still-active filter pills.
	refetchDocuments: () =>
		dispatch((innerDispatch, getState) => {
			const slice = getState().compliance_documents;
			const filters = nullSafeGetOrElse(`${DOCUMENTS_INDEX_TARGET_COLLECTION}.filters`, slice, {});
			const pagination = nullSafeGetOrElse(
				`${DOCUMENTS_INDEX_TARGET_COLLECTION}.pagination`,
				slice,
				INITIAL_PAGINATION
			);
			return innerDispatch(
				complianceDocumentsRestCrudThunks(ROLE_TYPES.SUPPLIER).read(
					{},
					DOCUMENTS_INDEX_TARGET_COLLECTION,
					pagination,
					null,
					filters
				)
			);
		}),
	// Both are redux-backed: the shell banner reads the summary and the Overview
	// badges read the client statuses, so refetching them here re-renders both.
	refreshComplianceState: () => {
		dispatch(fetchComplianceSummary(ROLE_TYPES.SUPPLIER)());
		dispatch(fetchComplianceClientStatuses(ROLE_TYPES.SUPPLIER)());
	},
	deleteComplianceDocument: (doc) =>
		dispatch(complianceDocumentsRestCrudThunks(ROLE_TYPES.SUPPLIER).deleteImmediate(doc, 'id')),
});

export default connect(mapStateToProps, mapDispatchToProps)(SupplierComplianceDocumentsPage);
